/**
 * Field / supplier display code derived from canonical `projectNumber`.
 * `PRJ-2026-1001` → `P26-1001`. Display-only — storage stays PRJ-YYYY-NNNN.
 */

const PRJ_RE = /^PRJ-(\d{4})-(\d{4})$/i;
const PHASE_LABEL_RE = /^(?:INT|EST|PRJ|DEC)-(\d{4})-(\d{4})(?:-[A-Z]{2})?(?:-R\d+)?$/i;
const FIELD_RE = /^P(\d{2})-(\d{1,4})$/i;
const BARE_SEQ_RE = /^(\d{1,4})$/;
const SEQ_MAX = 9999;

function formatFieldCode(year: number, seq: number): string {
  const yy = String(year % 100).padStart(2, '0');
  return `P${yy}-${String(seq).padStart(4, '0')}`;
}

export function toFieldCode(projectNumber: string | null | undefined): string | null {
  if (!projectNumber) return null;
  const match = projectNumber.trim().match(PRJ_RE);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const seq = parseInt(match[2], 10);
  if (!Number.isFinite(year) || !Number.isFinite(seq)) return null;
  return formatFieldCode(year, seq);
}

/** Best-effort: derive field code from a legacy phase label when number is missing. */
export function fieldCodeFromProjectLabel(projectLabel: string | null | undefined): string | null {
  if (!projectLabel) return null;
  const match = projectLabel.trim().match(PHASE_LABEL_RE);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const seq = parseInt(match[2], 10);
  if (!Number.isFinite(year) || !Number.isFinite(seq)) return null;
  return formatFieldCode(year, seq);
}

/**
 * Primary human project id for UI: prefer API `fieldCode`, else derive
 * from `projectNumber`, else derive from legacy `projectLabel`.
 */
export function displayProjectCode(opts: {
  fieldCode?: string | null;
  projectNumber?: string | null;
  projectLabel?: string | null;
  id?: string | null;
}): string {
  if (opts.fieldCode && opts.fieldCode.trim()) return opts.fieldCode.trim();
  const number = opts.projectNumber?.trim();
  if (number && FIELD_RE.test(number)) return number;
  const derived = toFieldCode(opts.projectNumber);
  if (derived) return derived;
  const fromLabel = fieldCodeFromProjectLabel(opts.projectLabel);
  if (fromLabel) return fromLabel;
  if (opts.projectLabel && opts.projectLabel.trim()) return opts.projectLabel.trim();
  if (opts.id) return opts.id.slice(0, 8);
  return '—';
}

export function parseFieldCode(
  input: string | null | undefined,
): { yearHint?: number; sequenceNumber: number } | null {
  if (!input) return null;
  const raw = input.trim();
  if (!raw) return null;

  const prj = raw.match(PRJ_RE);
  if (prj) {
    return {
      yearHint: parseInt(prj[1], 10),
      sequenceNumber: parseInt(prj[2], 10),
    };
  }

  const field = raw.match(FIELD_RE);
  if (field) {
    const yy = parseInt(field[1], 10);
    const sequenceNumber = parseInt(field[2], 10);
    if (!Number.isFinite(sequenceNumber) || sequenceNumber < 1 || sequenceNumber > SEQ_MAX) {
      return null;
    }
    const now = new Date().getFullYear();
    const century = Math.floor(now / 100) * 100;
    let yearHint = century + yy;
    if (yearHint > now + 1) yearHint -= 100;
    return { yearHint, sequenceNumber };
  }

  const bare = raw.match(BARE_SEQ_RE);
  if (bare) {
    const sequenceNumber = parseInt(bare[1], 10);
    if (!Number.isFinite(sequenceNumber) || sequenceNumber < 1 || sequenceNumber > SEQ_MAX) {
      return null;
    }
    return { sequenceNumber };
  }

  return null;
}
